import { useState, useEffect, useRef, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, Keyboard, X, QrCode, Barcode } from "lucide-react";

/**
 * useKeyboardWedge — detects rapid keystroke input from a hardware barcode scanner.
 * When characters arrive faster than `threshold` ms apart and end with Enter,
 * fires `onScan(value)`.
 */
export function useKeyboardWedge(onScan, { threshold = 50, minLength = 4 } = {}) {
  const buffer = useRef("");
  const lastKeyTime = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const now = Date.now();
      // If Enter/Tab pressed and buffer has content
      if ((e.key === "Enter" || e.key === "Tab") && buffer.current.length >= minLength) {
        e.preventDefault();
        onScan(buffer.current.trim());
        buffer.current = "";
        return;
      }
      // Ignore modifier keys
      if (e.key.length !== 1) return;
      // If too much time has passed, reset buffer
      if (now - lastKeyTime.current > threshold * 3) {
        buffer.current = "";
      }
      // If keys are arriving fast (scanner), accumulate
      if (now - lastKeyTime.current < threshold || buffer.current.length === 0) {
        buffer.current += e.key;
      } else {
        buffer.current = e.key; // Reset — too slow, likely typing
      }
      lastKeyTime.current = now;
      // Auto-fire if buffer grows large enough without Enter
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        if (buffer.current.length >= minLength) {
          onScan(buffer.current.trim());
          buffer.current = "";
        }
      }, threshold * 4);
    };
    window.addEventListener("keydown", handler);
    return () => { window.removeEventListener("keydown", handler); clearTimeout(timer.current); };
  }, [onScan, threshold, minLength]);
}

/**
 * BarcodeScannerModal — camera-based scanner using html5-qrcode.
 * Supports QR codes and Code 128 barcodes.
 */
export function BarcodeScannerModal({ open, onClose, onScan, title = "Scan Barcode" }) {
  const scannerRef = useRef(null);
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);

  const startScanner = useCallback(async () => {
    if (!containerRef.current || scannerRef.current) return;
    try {
      setError(null);
      const scanner = new Html5Qrcode("barcode-scanner-container");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
        (decodedText) => {
          onScan(decodedText);
          stopScanner();
          onClose();
        },
        () => {} // ignore errors during scanning
      );
      setScanning(true);
    } catch (err) {
      setError("Camera access denied or unavailable. Use manual entry or hardware scanner.");
      console.error("Scanner error:", err);
    }
  }, [onScan, onClose]);

  const stopScanner = useCallback(() => {
    if (scannerRef.current) {
      try { scannerRef.current.stop(); } catch {}
      try { scannerRef.current.clear(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => {
    if (open) {
      // Delay to let DOM render
      const t = setTimeout(startScanner, 300);
      return () => { clearTimeout(t); stopScanner(); };
    } else {
      stopScanner();
    }
  }, [open, startScanner, stopScanner]);

  // Cleanup on unmount
  useEffect(() => { return () => stopScanner(); }, [stopScanner]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { stopScanner(); onClose(); } }}>
      <DialogContent className="max-w-sm" data-testid="barcode-scanner-modal">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-1">
          <div id="barcode-scanner-container" ref={containerRef}
            className="w-full aspect-square rounded-lg overflow-hidden bg-zinc-900"
            style={{ minHeight: "250px" }} />
          {error && <div className="text-xs text-red-600 bg-red-50 p-2 rounded">{error}</div>}
          <div className="flex items-center gap-2 text-[10px] text-zinc-500">
            <QrCode className="w-3 h-3" /> QR Code
            <span className="text-zinc-300">|</span>
            <Barcode className="w-3 h-3" /> Code 128
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={() => { stopScanner(); onClose(); }}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * ScanInput — a text input that accepts both manual entry and hardware scanner input.
 * Shows a camera button to open the camera scanner modal.
 * Detects keyboard-wedge input automatically.
 */
export function ScanInput({ onScan, placeholder = "Scan or type...", className = "", autoFocus = false }) {
  const [value, setValue] = useState("");
  const [showCamera, setShowCamera] = useState(false);
  const inputRef = useRef(null);

  const handleScan = useCallback((code) => {
    setValue(code);
    onScan(code);
    setValue("");
  }, [onScan]);

  // Keyboard wedge detection on the input itself
  const lastKeyTime = useRef(0);
  const wedgeBuffer = useRef("");

  const handleKeyDown = (e) => {
    const now = Date.now();
    if (e.key === "Enter" && value.trim().length >= 3) {
      e.preventDefault();
      handleScan(value.trim());
      return;
    }
    // Detect rapid input (scanner)
    if (e.key.length === 1 && now - lastKeyTime.current < 50) {
      wedgeBuffer.current += e.key;
    } else if (e.key.length === 1) {
      wedgeBuffer.current = e.key;
    }
    lastKeyTime.current = now;
  };

  return (
    <>
      <div className={`flex gap-1.5 ${className}`}>
        <div className="relative flex-1">
          <QrCode className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
          <Input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="pl-8 h-9 text-sm font-mono"
            autoFocus={autoFocus}
            data-testid="scan-input"
          />
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowCamera(true)} className="h-9 px-2.5" data-testid="camera-scan-btn" title="Scan with camera">
          <Camera className="w-4 h-4" />
        </Button>
      </div>
      <BarcodeScannerModal open={showCamera} onClose={() => setShowCamera(false)} onScan={handleScan} />
    </>
  );
}

/**
 * ScanIndicator — shows a small badge indicating scanner mode is active.
 */
export function ScanIndicator({ active }) {
  if (!active) return null;
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded text-[10px] text-emerald-700 font-medium">
      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
      Scanner Ready
    </div>
  );
}
